/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import { TextFieldProps } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { NumberField, CheckboxField, DatePicker, OnOffCheckboxField, TextField } from '../../InputFields'
import { Select as SelectField } from '../../Select'
import { Slider } from '../../Slider'
import { FilterType } from '../ApiViewModels'
import moment from '../../../Libs/moment'
import { DateRangeWithLabel } from './DateRangeWithLabel'

const useStyles = makeStyles()(theme => ({
  filterElement: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    minWidth: `calc(25% - ${theme.spacing(2)})`,
    maxWidth: `calc(25% - ${theme.spacing(2)})`,
    alignItems: 'center',
    margin: theme.spacing(1),

    [theme.breakpoints.down('lg')]: {
      minWidth: `calc(33% - ${theme.spacing(2)})`,
      maxWidth: `calc(33% - ${theme.spacing(2)})`,
    },
    [theme.breakpoints.down('md')]: {
      minWidth: `calc(50% - ${theme.spacing(2)})`,
      maxWidth: `calc(50% - ${theme.spacing(2)})`,
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: `calc(100% - ${theme.spacing(2)})`,
      maxWidth: `calc(100% - ${theme.spacing(2)})`,
    },
  },
  checkboxElement: {
    paddingLeft: theme.spacing(2),
  },
}))

export interface FilterGeneratorProps {
  setFilterValue: (value: any) => void
  setFilterString: (value: string) => void
  label?: string
  name: string
  filterType: FilterType
  /** variant used by datePickers */
  variant?: TextFieldProps['variant']
  /** replaces built-in onChange for the current filter */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customOnChange?: (value: any) => void
  /** extends styles for current filter */
  className?: string
}

/**
 * This function returns a filter component wrapped in grid item according to the given types.
 */
export const FilterGenerator: React.VFC<FilterGeneratorProps> = ({
  setFilterValue,
  setFilterString,
  label,
  name,
  filterType,
  customOnChange,
  className,
}: FilterGeneratorProps) => {
  const { classes, cx } = useStyles()

  let filterComponent: React.ReactNode

  switch (filterType.type) {
    case 'numberFrom': {
      filterComponent = NumberField({
        ...filterType,
        resetValue: filterType.resetValue ?? filterType.min,
        label,
        onChange: value => {
          if (customOnChange) {
            customOnChange(value)
            return
          }

          setFilterString(value ? `${name}>=${value}` : '')
          setFilterValue(value)
        },
      })
      break
    }
    case 'numberTo':
      filterComponent = NumberField({
        ...filterType,
        resetValue: filterType.resetValue ?? filterType.max,
        label,
        onChange: value => {
          if (customOnChange) {
            customOnChange(value)
            return
          }

          setFilterString(value ? `${name}<=${value}` : '')
          setFilterValue(value)
        },
      })
      break
    case 'dateTo':
      filterComponent = DatePicker({
        ...filterType,
        initAfterRender: true,
        label,
        onChange: (value: moment.Moment | null) => {
          if (customOnChange) {
            customOnChange(value)
            return
          }

          setFilterString(value ? `${name}<=${moment(value).add(1, 'day').format('YYYY-MM-DD')}` : '')
          setFilterValue(value)
        },
      })
      break
    case 'dateFrom':
      filterComponent = DatePicker({
        ...filterType,
        label,
        initAfterRender: true,
        onChange: (value: moment.Moment | null) => {
          if (customOnChange) {
            customOnChange(value)
            return
          }

          setFilterString(value ? `${name}>=${moment(value).format('YYYY-MM-DD')}` : '')
          setFilterValue(value)
        },
      })
      break
    case 'date':
      filterComponent = DatePicker({
        ...filterType,
        label,
        initAfterRender: true,
        onChange: (value: moment.Moment | null) => {
          if (customOnChange) {
            customOnChange(value)
            return
          }

          setFilterString(value ? `${name}=${moment(value).format('YYYY-MM-DD')}` : '')
          setFilterValue(value)
        },
      })
      break
    case 'dateRange':
      filterComponent = DateRangeWithLabel({
        value: filterType.value,
        title: label,
        onChange: value => {
          if (customOnChange) {
            customOnChange(value)
            return
          }

          if (!value) {
            setFilterString('')
          } else {
            const { startDate, endDate } = value

            setFilterString(`${name}>=${moment(startDate).format('YYYY-MM-DD')},${name}<=${moment(endDate).add(1, 'day').format('YYYY-MM-DD')}`)
          }

          setFilterValue(value)
        },
      })
      break
    case 'select':
      filterComponent = SelectField({
        ...filterType,
        value: filterType.value ? filterType?.options?.filter(option => option.value === filterType.value)[0].value : undefined,
        label,
        onChange: (value: string) => {
          if (customOnChange) {
            customOnChange(value)
            return
          }

          setFilterString(value ? `${name}=${value}` : '')
          setFilterValue(value)
        },
        isSearchable: filterType.searchable ?? true,
        menuShouldBlockScroll: false,
        menuPortalTarget: null,
      })
      break
    case 'onOffCheckbox':
      filterComponent = OnOffCheckboxField({
        ...filterType,
        label,
        onChange: (value: [boolean, boolean]) => {
          if (customOnChange) {
            customOnChange(value)
            return
          }

          !value[0] && !value[1] ? setFilterString('') : setFilterString(`${name}=${value[0] ? filterType.mapping![0] : filterType.mapping![1]}`)
          setFilterValue(value)
        },
      })
      break
    case 'checkbox':
      filterComponent = CheckboxField({
        ...filterType,
        label,
        initAfterRender: true,
        onChange: (value: boolean) => {
          if (customOnChange) {
            customOnChange(value)
            return
          }

          setFilterString(value ? `${name}=${filterType.toFilter}` : '')
          setFilterValue(value)
        },
      })
      break
    case 'range':
      filterComponent = (
        <Slider
          {...filterType}
          valueLabelDisplay="auto"
          label={label}
          onChange={(value: number | number[] | undefined) => {
            if (customOnChange) {
              customOnChange(value)
              return
            }

            setFilterString(!value ? '' : `${name}>=${(value as number[])[0]},${name}<=${(value as number[])[1]}`)
            setFilterValue(value)
          }}
        />
      )
      break
    case 'internal':
      {
        const { type, ...filterTypeRest } = filterType
        filterComponent = TextField({
          ...filterTypeRest,
          label,
          onChange: (value: string) => {
            if (customOnChange) {
              customOnChange(value)
              return
            }

            setFilterString(value)
            setFilterValue(value)
          },
        })
      }
      break
    default: {
      const { type, ...filterTypeRest } = filterType

      filterComponent = TextField({
        ...filterTypeRest,
        label,
        onChange: (value: string) => {
          if (customOnChange) {
            customOnChange(value)
            return
          }

          setFilterString(value ? `${name}=*${value}*` : '')
          setFilterValue(value)
        },
      })
    }
  }

  return (
    <div
      className={cx(classes.filterElement, className, { [classes.checkboxElement]: filterType.type === 'checkbox' })}
      key={label + filterType.type}
    >
      {filterComponent}
    </div>
  )
}
